import {useEffect, useRef, useState} from 'react'
import {Link, useLocation, useSearchParams} from 'react-router-dom'
import {InformationCircleIcon} from '@heroicons/react/24/outline'
import {BackendApi} from '../../../../../api/api.ts'
import {User} from '../../../../../api/accessControl/User.ts'
import {ComboboxList} from '../../../../../components/general/ComboboxList.tsx'
import {PrimaryButton} from '../../../../../components/general/PrimaryButton.tsx'
import {SecondaryButton} from '../../../../../components/general/SecondaryButton.tsx'
import Loading from '../../../../../components/general/Loading.tsx'
import {Switch} from '../../../../../components/general/Switch.tsx'
import {fieldVariants, fieldLabelClasses} from '../../../../../components/general/fieldStyles.ts'
import {
    AdministrationOption,
    assignMapping,
    ClientAdministration,
    getMappingCoverage,
    linkAdministration,
    listAdministrationOptions,
    MappingCoverage,
    partialCoverageNote,
} from '../../../../../lib/payrollAdministration.ts'
import {
    createGLMapping,
    getGLMapping,
    GLMapping,
    GLMappingRule,
    listGLMappings,
    payrollMappingSettingsUrl,
    removeGLMappingRule,
    upsertGLMappingRule,
} from '../../../../../lib/payrollGLMapping.ts'
import {
    formatImportDate,
    hasAwaitingProviderRuns,
    ImportRecord,
    listImportHistory,
    runImport,
    RunImportResult,
} from '../../../../../lib/payrollImport.ts'
import {PayrollGLMappingEditor} from '../../../../../components/specific/PayrollGLMappingEditor/index.ts'
import {isAbortError} from '../../../../../lib/abortError.ts'
import {usePayrollAdministration} from './usePayrollAdministration.ts'

type AdministrationOptionsStatus = 'loading' | 'loaded' | 'failed'

type BusyAction = 'link' | 'switchMapping' | 'import'

const SYNC_FROM_RELOAD_DELAY_MS = 400
const MAX_SYNC_LOOKBACK_IN_YEARS = 5

interface Props {
    api: BackendApi
    user: User
    businessId: string
    providerName: string | null
}

function administrationDisplayName(opt: AdministrationOption): string {
    const base = `${opt.employerName} — ${opt.name}`
    return opt.number ? `${base} (${opt.number})` : base
}

function todayLocalDate(): string {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

function defaultSyncFrom(): string {
    return `${new Date().getFullYear()}-01-01`
}

function earliestSyncFrom(): string {
    return `${new Date().getFullYear() - MAX_SYNC_LOOKBACK_IN_YEARS}-01-01`
}

function isSyncFromInRange(value: string): boolean {
    return value !== '' && value >= earliestSyncFrom() && value <= todayLocalDate()
}

function ImportStatusBadge({status}: {status: string}) {
    const base = 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium'
    if (status === 'POSTED') return <span className={`${base} bg-green-100 text-green-700`}>Geboekt</span>
    if (status === 'STAGED') return <span className={`${base} bg-yellow-100 text-yellow-700`}>Wacht op koppeling</span>
    if (status === 'FAILED') return <span className={`${base} bg-red-100 text-red-700`}>Mislukt</span>
    if (status === 'AWAITING_PROVIDER') return <span className={`${base} bg-blue-100 text-blue-700`}>In voorbereiding</span>
    return <span className={`${base} bg-gray-100 text-gray-600`}>{status}</span>
}

export function ClientPayrollSection({api, user, businessId, providerName}: Props) {
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const currentTab = searchParams.get('tab') ?? 'trial-balance'

    const {administration, setAdministration, isLoading, loadError} = usePayrollAdministration(api, businessId)
    const [importHistory, setImportHistory] = useState<ImportRecord[]>([])

    const [administrationOptions, setAdministrationOptions] = useState<AdministrationOption[]>([])
    const [administrationOptionsStatus, setAdministrationOptionsStatus] = useState<AdministrationOptionsStatus>('loading')
    const administrationOptionsControllerRef = useRef<AbortController | null>(null)
    const [selectedOption, setSelectedOption] = useState<AdministrationOption | null>(null)

    const [mapping, setMapping] = useState<GLMapping | null>(null)
    const [rules, setRules] = useState<GLMappingRule[]>([])
    const [coverage, setCoverage] = useState<MappingCoverage | null>(null)
    const [coverageError, setCoverageError] = useState<string | null>(null)
    const [isAdministrationDataLoading, setIsAdministrationDataLoading] = useState(false)
    const [isCoverageLoading, setIsCoverageLoading] = useState(false)
    const coverageControllerRef = useRef<AbortController | null>(null)
    const [existingMappings, setExistingMappings] = useState<GLMapping[]>([])
    const [selectedMappingId, setSelectedMappingId] = useState<string | null>(null)

    const [runResults, setRunResults] = useState<RunImportResult[] | null>(null)
    const [syncFrom, setSyncFrom] = useState<string>(defaultSyncFrom)
    const [busyAction, setBusyAction] = useState<BusyAction | null>(null)
    const isBusy = busyAction !== null
    const [actionError, setActionError] = useState<string | null>(null)

    const [showNewMappingRow, setShowNewMappingRow] = useState(false)
    const [newMappingName, setNewMappingName] = useState('')
    const [isCreatingMapping, setIsCreatingMapping] = useState(false)
    const newMappingInputRef = useRef<HTMLInputElement>(null)
    const pendingCreatedMappingRef = useRef<GLMapping | null>(null)

    const [autoSyncEnabled, setAutoSyncEnabled] = useState(false)
    const [isAutoSyncLoading, setIsAutoSyncLoading] = useState(true)
    const [isAutoSyncSaving, setIsAutoSyncSaving] = useState(false)
    const [autoSyncError, setAutoSyncError] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        setIsAutoSyncLoading(true)
        setAutoSyncError(null)
        api.Partner__business__settings(businessId)
            .then(response => {
                if (cancelled) return
                setAutoSyncEnabled(response.settings['PAYROLL_AUTO_SYNC_ENABLED'] === true)
            })
            .catch(() => {
                if (!cancelled) setAutoSyncError('Instelling kon niet worden geladen.')
            })
            .finally(() => {
                if (!cancelled) setIsAutoSyncLoading(false)
            })
        return () => { cancelled = true }
    }, [api, businessId])

    async function handleAutoSyncToggle(next: boolean) {
        const previous = autoSyncEnabled
        setAutoSyncEnabled(next)
        setIsAutoSyncSaving(true)
        setAutoSyncError(null)
        try {
            await api.Partner__business__updateSetting({
                business_id: businessId,
                setting_name: 'PAYROLL_AUTO_SYNC_ENABLED',
                setting_value: next,
            })
        } catch {
            setAutoSyncEnabled(previous)
            setAutoSyncError('Instelling opslaan mislukt. Probeer het opnieuw.')
        } finally {
            setIsAutoSyncSaving(false)
        }
    }

    useEffect(() => {
        let cancelled = false

        setSelectedOption(null)
        setMapping(null)
        setRules([])
        setCoverage(null)
        setSelectedMappingId(null)
        setImportHistory([])
        setRunResults(null)
        setActionError(null)
        setShowNewMappingRow(false)
        setNewMappingName('')
        setIsCoverageLoading(false)
        pendingCreatedMappingRef.current = null

        void loadAdministrationOptions()
        listGLMappings(api)
            .catch(() => [] as GLMapping[])
            .then(mappingList => { if (!cancelled) setExistingMappings(mappingList) })
        return () => {
            cancelled = true
            administrationOptionsControllerRef.current?.abort()
            coverageControllerRef.current?.abort()
        }
    }, [businessId])

    async function loadAdministrationOptions() {
        administrationOptionsControllerRef.current?.abort()
        const controller = new AbortController()
        administrationOptionsControllerRef.current = controller
        setAdministrationOptionsStatus('loading')
        try {
            const options = await listAdministrationOptions(api, controller.signal)
            if (controller.signal.aborted) return
            setAdministrationOptions(options)
            setAdministrationOptionsStatus('loaded')
        } catch (error: unknown) {
            if (isAbortError(error) || controller.signal.aborted) return
            setAdministrationOptions([])
            setAdministrationOptionsStatus('failed')
        }
    }

    useEffect(() => {
        if (isLoading || !administration) return
        let cancelled = false

        async function loadAdministrationData() {
            setIsAdministrationDataLoading(true)
            try {
                await listImportHistory(api, administration.administrationId)
                    .then((h) => { if (!cancelled) setImportHistory(h) })
                    .catch(() => undefined)
                if (cancelled) return
                if (administration.mappingId) {
                    await loadMappingAndCoverage(administration.mappingId, administration.administrationId)
                } else {
                    const mappingList = await listGLMappings(api).catch(() => [] as GLMapping[])
                    if (!cancelled) await autoAssignOrCreateMapping(administration.administrationId, mappingList)
                }
            } finally {
                if (!cancelled) setIsAdministrationDataLoading(false)
            }
        }
        void loadAdministrationData()
        return () => {
            cancelled = true
            setIsAdministrationDataLoading(false)
        }
    }, [isLoading, administration?.administrationId])

    useEffect(() => {
        if (!administration || !selectedMappingId) return
        if (!isSyncFromInRange(syncFrom)) {
            coverageControllerRef.current?.abort()
            setIsCoverageLoading(false)
            setCoverage(null)
            setCoverageError(`Kies een datum tussen ${earliestSyncFrom()} en vandaag.`)
            return
        }
        setIsCoverageLoading(true)
        const timer = setTimeout(() => {
            void loadMappingAndCoverage(selectedMappingId, administration.administrationId)
        }, SYNC_FROM_RELOAD_DELAY_MS)
        return () => clearTimeout(timer)
    }, [syncFrom])

    async function loadMappingAndCoverage(mappingId: string, administrationId: string, mappingList?: GLMapping[]) {
        coverageControllerRef.current?.abort()
        const controller = new AbortController()
        coverageControllerRef.current = controller
        setIsCoverageLoading(true)
        try {
            const [loaded, covResult] = await Promise.all([
                getGLMapping(api, mappingId),
                getMappingCoverage(api, administrationId, syncFrom, controller.signal)
                    .then(cov => ({ok: true as const, cov}))
                    .catch(() => ({ok: false as const, cov: null})),
            ])
            if (controller.signal.aborted) return
            setMapping(loaded.mapping)
            setRules(loaded.rules)
            if (covResult.ok) {
                setCoverage(covResult.cov)
                setCoverageError(null)
            } else {
                setCoverage(null)
                setCoverageError('Kon de grootboekdekking niet laden. Ververs de pagina en probeer het opnieuw.')
            }
            setSelectedMappingId(mappingId)
            if (mappingList) {
                setExistingMappings(mappingList)
            }
        } catch (error: unknown) {
            if (controller.signal.aborted) return
            throw error
        } finally {
            if (coverageControllerRef.current === controller) setIsCoverageLoading(false)
        }
    }

    async function autoAssignOrCreateMapping(administrationId: string, mappingList: GLMapping[]) {
        if (mappingList.length === 0) {
            const created = pendingCreatedMappingRef.current ?? await createGLMapping(api, 'Standaard')
            pendingCreatedMappingRef.current = created
            setExistingMappings([created])
            await assignMapping(api, administrationId, created.mappingId)
            pendingCreatedMappingRef.current = null
            await loadMappingAndCoverage(created.mappingId, administrationId, [created])
        } else {
            const last = mappingList[mappingList.length - 1]
            await assignMapping(api, administrationId, last.mappingId)
            await loadMappingAndCoverage(last.mappingId, administrationId, mappingList)
        }
    }

    async function handleLinkAdministration() {
        if (!selectedOption) return
        setBusyAction('link')
        setActionError(null)
        try {
            const linked = await linkAdministration(api, businessId, selectedOption.employerRef, selectedOption.ref)
            const admin: ClientAdministration = {
                administrationId: linked.administrationId,
                externalPayrollRef: linked.externalPayrollRef,
                externalEmployerRef: linked.externalEmployerRef,
                administrationName: linked.administrationName,
                administrationNumber: linked.administrationNumber,
                mappingId: null,
                mappingName: null,
            }
            setAdministration(admin)
            setSelectedOption(null)
        } catch (e: unknown) {
            const detail = (e as {errorDetail?: {detail?: string}})?.errorDetail?.detail
            setActionError(detail ?? 'Koppelen aan administratie mislukt.')
        } finally {
            setBusyAction(null)
        }
    }

    async function handleSwitchMapping(m: GLMapping | null) {
        if (!m || !administration) return
        setBusyAction('switchMapping')
        setActionError(null)
        try {
            await assignMapping(api, administration.administrationId, m.mappingId)
            await loadMappingAndCoverage(m.mappingId, administration.administrationId)
        } catch {
            setActionError('Koppeling wisselen mislukt.')
        } finally {
            setBusyAction(null)
        }
    }

    async function handleRunImport() {
        if (!administration) return
        if (!syncFrom) {
            setActionError('Kies een datum om vanaf te importeren.')
            return
        }
        if (syncFrom > todayLocalDate()) {
            setActionError('De datum mag niet in de toekomst liggen.')
            return
        }
        if (syncFrom < earliestSyncFrom()) {
            setActionError(`De datum mag niet vóór ${earliestSyncFrom()} liggen.`)
            return
        }
        setBusyAction('import')
        setActionError(null)
        setRunResults(null)
        try {
            const results = await runImport(api, administration.administrationId, syncFrom)
            setRunResults(results)
            const history = await listImportHistory(api, administration.administrationId)
            setImportHistory(history)
        } catch (e: unknown) {
            const detail = (e as {errorDetail?: {detail?: string}})?.errorDetail?.detail
            setActionError(detail ?? 'Import mislukt. Probeer het opnieuw.')
        } finally {
            setBusyAction(null)
        }
    }

    function handleRuleUpserted(rule: GLMappingRule) {
        setRules(prev => {
            const idx = prev.findIndex(r => r.externalGlAccountCode === rule.externalGlAccountCode)
            if (idx >= 0) {
                const next = [...prev]
                next[idx] = rule
                return next
            }
            return [...prev, rule]
        })
    }

    function handleRuleRemoved(mappingId: string, code: string) {
        setRules(prev => prev.filter(r => !(r.mappingId === mappingId && r.externalGlAccountCode === code)))
    }

    async function handleUpsertRule(code: string, description: string, glAccountId: string) {
        if (!mapping) return
        const rule = await upsertGLMappingRule(api, mapping.mappingId, code, description, glAccountId)
        handleRuleUpserted(rule)
    }

    async function handleRemoveRule(code: string) {
        if (!mapping) return
        await removeGLMappingRule(api, mapping.mappingId, code)
        handleRuleRemoved(mapping.mappingId, code)
    }

    async function handleCreateMapping() {
        if (!administration || !newMappingName.trim()) return
        setIsCreatingMapping(true)
        setActionError(null)
        try {
            const created = pendingCreatedMappingRef.current ?? await createGLMapping(api, newMappingName.trim())
            pendingCreatedMappingRef.current = created
            const updatedMappings = existingMappings.some(m => m.mappingId === created.mappingId)
                ? existingMappings
                : [...existingMappings, created]
            setExistingMappings(updatedMappings)
            await assignMapping(api, administration.administrationId, created.mappingId)
            pendingCreatedMappingRef.current = null
            await loadMappingAndCoverage(created.mappingId, administration.administrationId, updatedMappings)
            setNewMappingName('')
            setShowNewMappingRow(false)
        } catch {
            setActionError('Aanmaken koppeling mislukt. De koppeling is aangemaakt; probeer deze opnieuw te selecteren.')
        } finally {
            setIsCreatingMapping(false)
        }
    }

    function formatPeriodEnd(isoDate: string): string {
        const [year, month, day] = isoDate.split('-')
        return `${day}-${month}-${year}`
    }

    function buildReturnTo(): string {
        return `${location.pathname}?tab=${currentTab}&payroll=open`
    }

    function mappingSettingsUrl(mappingId: string): string {
        return payrollMappingSettingsUrl(mappingId, {
            administrationId: administration?.administrationId,
            returnTo: buildReturnTo(),
        })
    }

    const hasPostedImport = importHistory.some(r => r.status === 'POSTED')
    const showAwaitingProviderNote = hasAwaitingProviderRuns(importHistory)
    const inSteadyState = !!(administration && importHistory.length > 0)
    const coverageOk = coverage !== null && coverage.allGlAccounts.every(a => rules.some(r => r.externalGlAccountCode === a.code))
    const providerLabel = providerName ?? 'je salarispakket'
    const isGlMappingLoading = isAdministrationDataLoading || isCoverageLoading
    const coverageLoadingMessage = isGlMappingLoading
        ? `Grootboekrekeningen ophalen uit ${providerLabel}… dit kan tot een halve minuut duren`
        : null
    const isImporting = busyAction === 'import'

    const autoSyncSection = (
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 pb-6">
            <div>
                <p className="text-sm font-medium text-gray-900">
                    Salarismutaties automatisch synchroniseren
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                    Nieuwe loonjournaalposten worden dan één keer per dag automatisch geïmporteerd.
                </p>
                {autoSyncError && <p className="text-sm text-red-600 mt-1">{autoSyncError}</p>}
            </div>
            <Switch
                aria-label="Salarismutaties automatisch synchroniseren"
                checked={autoSyncEnabled}
                onChange={handleAutoSyncToggle}
                disabled={isAutoSyncLoading || isAutoSyncSaving}
            />
        </div>
    )

    if (isLoading) {
        return <p className="text-sm text-gray-500">Laden…</p>
    }

    if (loadError) {
        return <p className="text-sm text-red-600">{loadError}</p>
    }

    if (inSteadyState && administration) {
        const adminLabel = administration.administrationName ?? administration.externalPayrollRef
        const adminDisplay = administration.administrationNumber
            ? `${adminLabel} (${administration.administrationNumber})`
            : adminLabel
        return (
            <div className="space-y-6">
                {autoSyncSection}
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-sm text-gray-700">
                            <span className="font-medium text-gray-900">Administratie:</span>{' '}
                            {adminDisplay}
                        </p>
                        {administration.mappingId && administration.mappingName && (
                            <p className="text-sm text-gray-700 mt-1">
                                <span className="font-medium text-gray-900">Grootboekkoppeling:</span>{' '}
                                <Link
                                    to={mappingSettingsUrl(administration.mappingId)}
                                    className="text-primary underline underline-offset-2 hover:text-primary/80"
                                >
                                    {administration.mappingName}
                                </Link>
                            </p>
                        )}
                        {administration.mappingId && (
                            <p className="text-xs text-gray-400 mt-0.5">Wijzigingen gelden voor volgende imports.</p>
                        )}
                    </div>
                </div>

                {actionError && <p className="text-sm text-red-600">{actionError}</p>}

                <div className="flex items-end gap-3">
                    <div className="w-44">
                        <label className={fieldLabelClasses} htmlFor="payroll-sync-from">
                            Importeren vanaf
                        </label>
                        <input
                            id="payroll-sync-from"
                            type="date"
                            value={syncFrom}
                            min={earliestSyncFrom()}
                            max={todayLocalDate()}
                            onChange={e => setSyncFrom(e.target.value)}
                            disabled={isBusy}
                            className={fieldVariants()}
                        />
                    </div>
                    <PrimaryButton
                        title={isImporting ? 'Importeren…' : 'Importeren'}
                        onClick={handleRunImport}
                        disabled={isBusy}
                        loading={isImporting}
                    />
                </div>

                {showAwaitingProviderNote && (
                    <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                        Sommige runs worden nog voorbereid door het salarispakket. Ze worden vannacht automatisch
                        geïmporteerd, of bij de volgende import.
                    </p>
                )}

                {runResults !== null && runResults.length === 0 && (
                    <p className="text-sm text-gray-500 italic">
                        Geen loonjournaalposten gevonden voor de huidige periode.
                    </p>
                )}

                {runResults !== null && runResults.length > 0 && (
                    <div className="space-y-3">
                        {runResults.map(result => (
                            <div key={result.runRef} className="border rounded-md p-3 bg-white">
                                <div className="flex items-center gap-4">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-900">
                                            {result.periodStart} — {result.periodEnd}
                                        </p>
                                    </div>
                                    <ImportStatusBadge status={result.status} />
                                </div>
                                {result.unmappedGlAccounts.length > 0 && administration.mappingId && (
                                    <div className="mt-2 space-y-1">
                                        <p className="text-xs text-amber-700 font-medium">Ongekoppelde grootboekrekeningen:</p>
                                        {result.unmappedGlAccounts.map(a => (
                                            <Link
                                                key={a.code}
                                                to={mappingSettingsUrl(administration.mappingId!)}
                                                className="block text-xs text-amber-700 hover:underline"
                                            >
                                                {a.code} — {a.description}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {importHistory.length > 0 && (
                    <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Importgeschiedenis</h4>
                        <div className="space-y-2">
                            {importHistory.map(record => (
                                <div
                                    key={record.importId}
                                    className="flex items-center gap-4 border rounded-md p-3 bg-white"
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-900">
                                            {record.periodStart} — {record.periodEnd}
                                            <span className="ml-2 text-xs text-gray-400">
                                                run {record.runSequenceNumber}
                                            </span>
                                        </p>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            {record.status === 'POSTED'
                                                ? <>Boekdatum {formatPeriodEnd(record.periodEnd)} · Geïmporteerd {formatImportDate(record.postedAt)}</>
                                                : <>Aangemaakt {formatImportDate(record.createdAt)}</>
                                            }
                                        </p>
                                    </div>
                                    <ImportStatusBadge status={record.status} />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="space-y-8">
            {administration && autoSyncSection}

            {!administration && (
                <section>
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Loonadministratie koppelen</h4>
                    <div className="space-y-3 max-w-md">
                        <ComboboxList
                            label={providerName ? `Administratie (${providerName})` : 'Administratie'}
                            options={administrationOptions}
                            onSelect={o => setSelectedOption(o as AdministrationOption | null)}
                            selected={selectedOption}
                            emptySelectionMessage={administrationOptionsStatus === 'loading'
                                ? 'Administraties laden…'
                                : 'Zoek op naam, nummer of werkgever...'}
                            disabled={isBusy || administrationOptionsStatus !== 'loaded'}
                            internalMapper={(o: AdministrationOption) => ({
                                ...o,
                                displayName: administrationDisplayName(o),
                                id: o.ref,
                            })}
                            showClearButton={true}
                        />
                        {administrationOptionsStatus === 'failed' && (
                            <div className="flex items-center justify-between gap-3">
                                <p className="text-sm text-red-600">
                                    Kon de administraties niet ophalen uit {providerLabel}. Probeer het opnieuw.
                                </p>
                                <SecondaryButton
                                    title="Opnieuw proberen"
                                    onClick={() => void loadAdministrationOptions()}
                                />
                            </div>
                        )}
                        {administrationOptionsStatus === 'loaded' && administrationOptions.length === 0 && (
                            <p className="text-sm text-gray-500">
                                Geen administraties gevonden in {providerLabel}.
                            </p>
                        )}
                        <PrimaryButton
                            title={busyAction === 'link' ? 'Koppelen…' : 'Koppelen'}
                            onClick={handleLinkAdministration}
                            disabled={isBusy || !selectedOption || administrationOptionsStatus !== 'loaded'}
                            loading={busyAction === 'link'}
                        />
                        {actionError && <p className="text-sm text-red-600">{actionError}</p>}
                    </div>
                </section>
            )}

            {administration && (
                <section className="space-y-4">
                    <div className="flex items-center gap-2">
                        <InformationCircleIcon className="h-4 w-4 text-gray-400 shrink-0" />
                        <p className="text-sm text-gray-500">
                            Met de grootboekkoppeling bepaal je op welke grootboekrekeningen de loonjournaalposten uit {providerName ?? 'je salarispakket'} geboekt worden. Een koppeling kan door meerdere klanten gebruikt worden.
                        </p>
                    </div>

                    {!mapping && coverageLoadingMessage && (
                        <div className="text-sm text-gray-500">
                            <Loading message={coverageLoadingMessage}/>
                        </div>
                    )}

                    {mapping && (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between gap-3">
                                <label className="block text-sm font-medium text-gray-700">
                                    Actieve koppeling
                                </label>
                                {!showNewMappingRow && (
                                    <SecondaryButton
                                        title="Nieuwe koppeling"
                                        onClick={() => {
                                            setShowNewMappingRow(true)
                                            setNewMappingName('')
                                            setTimeout(() => newMappingInputRef.current?.focus(), 0)
                                        }}
                                        disabled={isBusy || isCreatingMapping}
                                    />
                                )}
                            </div>
                            <ComboboxList
                                label=""
                                options={existingMappings}
                                onSelect={m => handleSwitchMapping(m as GLMapping | null)}
                                selected={existingMappings.find(m => m.mappingId === selectedMappingId) ?? null}
                                emptySelectionMessage="Selecteer een koppeling..."
                                internalMapper={(m: GLMapping) => ({...m, displayName: m.name, id: m.mappingId})}
                                showClearButton={false}
                            />
                            {showNewMappingRow && (
                                <div className="flex items-center gap-2">
                                    <input
                                        ref={newMappingInputRef}
                                        type="text"
                                        placeholder="bijv. Standaard"
                                        value={newMappingName}
                                        onChange={e => setNewMappingName(e.target.value)}
                                        disabled={isCreatingMapping}
                                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                                    />
                                    <PrimaryButton
                                        title={isCreatingMapping ? 'Aanmaken…' : 'Aanmaken'}
                                        onClick={handleCreateMapping}
                                        disabled={!newMappingName.trim()}
                                        loading={isCreatingMapping}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewMappingRow(false)}
                                        disabled={isCreatingMapping}
                                        className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
                                    >
                                        Annuleren
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {!coverageLoadingMessage && coverage && partialCoverageNote(coverage) && (
                        <p className="text-xs text-gray-500 italic">{partialCoverageNote(coverage)}</p>
                    )}

                    {mapping && (
                        <PayrollGLMappingEditor
                            user={user}
                            mapping={mapping}
                            rules={rules}
                            coverage={coverage ? {
                                allGlAccounts: coverage.allGlAccounts,
                                unmappedGlAccounts: coverage.unmappedGlAccounts,
                            } : null}
                            usedByCount={mapping.usedByCount}
                            providerName={providerName}
                            coverageLoadingMessage={coverageLoadingMessage}
                            onUpsertRule={handleUpsertRule}
                            onRemoveRule={handleRemoveRule}
                        />
                    )}

                    {actionError && <p className="text-sm text-red-600">{actionError}</p>}
                </section>
            )}

            {administration && mapping && (
                <section>
                    <div className="flex items-end gap-3">
                        <div className="w-44">
                            <label className={fieldLabelClasses} htmlFor="payroll-sync-from-first">
                                Importeren vanaf
                            </label>
                            <input
                                id="payroll-sync-from-first"
                                type="date"
                                value={syncFrom}
                                min={earliestSyncFrom()}
                            max={todayLocalDate()}
                                onChange={e => setSyncFrom(e.target.value)}
                                disabled={isBusy}
                                className={fieldVariants()}
                            />
                        </div>
                        <PrimaryButton
                            title={isImporting ? 'Importeren…' : 'Start eerste synchronisatie'}
                            onClick={handleRunImport}
                            disabled={isBusy || isGlMappingLoading || !coverageOk}
                            loading={isImporting}
                            disabledFeedback={isGlMappingLoading
                                ? 'Grootboekrekeningen worden nog opgehaald'
                                : 'Koppel eerst alle grootboekrekeningen'}
                        />
                    </div>

                    {coverageError && !isGlMappingLoading && (
                        <p className="mt-2 text-sm text-red-600">{coverageError}</p>
                    )}

                    {showAwaitingProviderNote && (
                        <p className="mt-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                            Sommige runs worden nog voorbereid door het salarispakket. Ze worden vannacht automatisch
                            geïmporteerd, of bij de volgende import.
                        </p>
                    )}

                    {runResults !== null && runResults.length === 0 && (
                        <p className="mt-4 text-sm text-gray-500 italic">
                            Geen loonjournaalposten gevonden voor de huidige periode.
                        </p>
                    )}

                    {runResults !== null && runResults.length > 0 && (
                        <div className="mt-4 space-y-3">
                            {runResults.map(result => (
                                <div key={result.runRef} className="border rounded-md p-3 bg-white">
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-900">
                                                {result.periodStart} — {result.periodEnd}
                                            </p>
                                        </div>
                                        <ImportStatusBadge status={result.status} />
                                    </div>
                                    {result.unmappedGlAccounts.length > 0 && (
                                        <div className="mt-2 space-y-1">
                                            <p className="text-xs text-amber-700 font-medium">Ongekoppelde grootboekrekeningen:</p>
                                            {result.unmappedGlAccounts.map(a => (
                                                <span key={a.code} className="block text-xs text-amber-700">
                                                    {a.code} — {a.description}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            )}
        </div>
    )
}
